import { useNavigate, useParams } from 'react-router-dom';
import { CalendarPlusIcon, CopyPlusIcon, DownloadIcon, MailIcon } from 'lucide-react';
import { PageHeader } from '../ui/PageHeader';
import { MissingItem } from '../ui/MissingItem';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { ImageField } from '../ui/ImageField';
import { TagInput } from '../ui/TagInput';
import { DeleteButton } from '../ui/DeleteButton';
import { undoToast } from '../ui/undoToast';
import { Spinner } from '../../ui/Spinner';
import { ProductImage } from '../../ui/ProductImage';
import { ScaledDesign } from './ScaledDesign';
import { PaletteSelect } from './PaletteSelect';
import { CopyButton } from './CopyButton';
import { useAdmin } from '../AdminContext';
import { useDesignDownload } from './useDesignDownload';
import { useSocialPosts } from '../social/useSocialPosts';
import { useNewsletters } from '../newsletter/useNewsletters';
import { newslettersUsingDesign } from '../newsletter/newsletterRules';
import { platformMeta } from '../social/socialOptions';
import { brandKit as defaultKit } from './marketingSeed';
import { templateByKey, templates } from './marketingOptions';
import { fillTokens, marketingTokens } from './marketingRules';
import { firstFreeDay, postFromDesign } from './designPlanning';
import { btnPrimary, btnSecondary, inputCls } from '../ui/styles';
import { clone, uniqueSlug } from '../editing';
import { plural } from '../../core/format/text';
import type { DesignField, MarketingDesign, TemplateKey } from '../../core/types/marketing';
type TextField = 'eyebrow' | 'headline' | 'accent' | 'body' | 'cta';
const labels: Record<TextField, {
  label: string;
  hint?: string;
}> = {
  eyebrow: {
    label: 'Regeltje boven de kop',
    hint: 'Klein, in de accentkleur. Leeg laten mag.'
  },
  headline: {
    label: 'Kop'
  },
  accent: {
    label: 'Cursief deel',
    hint: 'Komt schuin, in de accentkleur, onder de kop.'
  },
  body: {
    label: 'Tekst'
  },
  cta: {
    label: 'Oproep of voetregel'
  }
};

/** De tekst onder een post: dezelfde limiet als een Instagram-post in de social planner. */
const CAPTION_MAX = platformMeta['Instagram-post'].captionMax;
/** Zoveel hashtags uit de merkstijl tonen we als suggestie. */
const HASHTAG_SUGGESTIONS = 10;
const isTemplateKey = (value: string): value is TemplateKey => templates.some(t => t.key === value);

/** Eén ontwerp aanpassen, met live voorbeeld en download op ware grootte. */
export function MarketingEditor() {
  const {
    id = ''
  } = useParams();
  const navigate = useNavigate();
  const {
    marketing,
    setMarketing,
    catalog,
    site
  } = useAdmin();
  const {
    canvas,
    busy,
    download: downloadDesign
  } = useDesignDownload();
  const {
    createPost
  } = useSocialPosts();
  const {
    createIssue
  } = useNewsletters();
  const design = marketing.designs.find(d => d.id === id);
  if (!design) {
    return <MissingItem title="Ontwerp niet gevonden" back={{
      to: '/be/ontwerpen',
      label: 'Alle ontwerpen'
    }} data-id="element-3010" data-mp="3884xkpobpxvi" />;
  }
  const t = templateByKey(design.template);
  const has = (f: DesignField) => t.fields.includes(f);
  const tokens = marketingTokens(site);
  const set = (patch: Partial<MarketingDesign>) => setMarketing(m => ({
    ...m,
    designs: m.designs.map(d => d.id === id ? {
      ...d,
      ...patch
    } : d)
  }));
  const campaigns = Array.from(new Set(marketing.designs.map(d => d.campaign).filter(Boolean)));
  const photos = [{
    src: site.founder.portrait,
    label: 'Portret'
  }, {
    src: site.founder.atWork,
    label: 'Aan het werk'
  }, {
    src: site.founder.withProducts,
    label: 'Met producten'
  }, {
    src: site.founder.portraitPalm,
    label: 'Portret buiten'
  }, ...catalog.routines.map(r => ({
    src: r.image,
    label: r.name
  }))].filter(p => p.src);
  const fullCaption = [fillTokens(design.caption, tokens), design.hashtags.join(' ')].filter(Boolean).join('\n\n');
  const hashtagPool = marketing.brandKit.hashtags.length ? marketing.brandKit.hashtags : defaultKit.hashtags;
  const duplicate = () => {
    const copyId = uniqueSlug(`${design.id}-kopie`, marketing.designs.map(d => d.id));
    setMarketing(m => ({
      ...m,
      designs: [{
        ...clone(design),
        id: copyId,
        name: `${design.name} (kopie)`
      }, ...m.designs]
    }));
    navigate(`/be/ontwerpen/${copyId}`);
  };

  // Zelfde regels als "Inplannen" in de planner: tekst en formaat uit het ontwerp, eerste vrije dag.
  const planAsPost = () => createPost({
    ...postFromDesign(design),
    date: firstFreeDay(marketing.posts)
  });

  /** Een e-mailontwerp als kop van een nieuwe nieuwsbrief, met een begin- en slotzin eronder. */
  const startNewsletter = () => createIssue({
    subject: fillTokens(design.headline, tokens),
    blocks: [{
      id: 'b1',
      kind: 'design',
      designId: design.id
    }, {
      id: 'b2',
      kind: 'text',
      heading: '',
      body: ''
    }, {
      id: 'b3',
      kind: 'text',
      heading: '',
      body: 'Liefs,\n{firstName}'
    }]
  });
  const usedInPosts = marketing.posts.filter(p => p.designId === id).length;
  const usedInNewsletters = newslettersUsingDesign(marketing.newsletters, id).length;
  const usage = [usedInPosts ? plural(usedInPosts, 'post', 'posts') : '', usedInNewsletters ? plural(usedInNewsletters, 'nieuwsbrief', 'nieuwsbrieven') : ''].filter(Boolean);
  const textInput = (f: TextField) => {
    if (!has(f)) return null;
    const value = design[f];
    const filled = fillTokens(value, tokens);
    return <Field key={f} label={labels[f].label} hint={labels[f].hint} data-id="element-3011" data-mp="1chid0co1492e">
        {f === 'body' ? <textarea value={value} onChange={e => set({
        [f]: e.target.value
      })} rows={3} className={`${inputCls} resize-y leading-relaxed`} data-id="element-3012" data-mp="orxs3o8bf1qh" /> : <input value={value} onChange={e => set({
        [f]: e.target.value
      })} className={inputCls} data-id="element-3013" data-mp="xk9jpdf60v81" />}
        {filled !== value && <span className="mt-1.5 block text-[12px] text-muted" data-id="element-3014" data-mp="srggitzchuy2">
            <span className="text-sage" data-id="element-3015" data-mp="52nfij3v8wwl">In het ontwerp:</span> {filled}
          </span>}
      </Field>;
  };
  return <div data-id="element-3016" data-mp="11bxaynbndyf">
      <PageHeader title={design.name || 'Naamloos ontwerp'} description={`${t.name} · ${t.sizeLabel}. ${t.use}`} back={{
      to: '/be/ontwerpen',
      label: 'Alle ontwerpen'
    }} actions={<>
            <button type="button" onClick={duplicate} className={btnSecondary} data-id="element-3018" data-mp="29weparh38tyx">
              <CopyPlusIcon className="h-4 w-4" strokeWidth={1.75} data-id="element-3019" data-mp="29yhoo244rzgb" />
              Dupliceer
            </button>
            {t.channel === 'Instagram' && <button type="button" onClick={planAsPost} className={btnSecondary} data-id="element-3020" data-mp="1h8snnorf7zep">
                <CalendarPlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3021" data-mp="1m60wgyqoir3a" />
                Inplannen
              </button>}
            {t.channel === 'E-mail' && <button type="button" onClick={startNewsletter} className={btnSecondary} data-id="element-3022" data-mp="1w59z0fzm1e19">
                <MailIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3023" data-mp="32llo7rkgflkp" />
                In nieuwsbrief
              </button>}
            <button type="button" onClick={() => downloadDesign(design)} disabled={busy} className={btnPrimary} data-id="element-3024" data-mp="37ic4aew9eh64">
              {busy ? <Spinner data-id="element-3025" data-mp="vymn894mzrkp" /> : <DownloadIcon className="h-4 w-4" strokeWidth={1.75} data-id="element-3026" data-mp="qxyy5szyxkt4" />}
              {busy ? 'Bezig…' : 'Download PNG'}
            </button>
          </>} data-id="element-3017" data-mp="11ldxvmh88rg" />

      <div className="grid gap-6 lg:grid-cols-12" data-id="element-3027" data-mp="m55r2yh120dv">
        {/* Voorbeeld */}
        <div className="lg:col-span-7" data-id="element-3028" data-mp="h748eenik32x">
          <div className="rounded-lg bg-white/60 p-4 ring-1 ring-line sm:p-8 lg:sticky lg:top-20" data-id="element-3029" data-mp="c8ufkap9hekh">
            <ScaledDesign ref={canvas} design={design} maxHeight={640} data-id="element-3030" data-mp="878va4aedy09" />
            <p className="mt-4 text-center text-[12px] text-muted" data-id="element-3031" data-mp="85xso7souwaq">
              Download: {t.w * t.exportScale} × {t.h * t.exportScale} px
              {t.channel === 'Print' ? ' · geschikt om te laten drukken' : ''}
            </p>
            {usage.length > 0 &&
          // Aanpassen werkt overal door: dat moet ze weten vóór ze iets verandert.
          <p className="mt-1 text-center text-[12px] text-muted" data-id="element-3032" data-mp="1smsz9h3w1m90">Gebruikt in {usage.join(' en ')}; wijzigingen gaan daar mee.</p>}
          </div>
        </div>

        {/* Velden */}
        <div className="space-y-6 lg:col-span-5" data-id="element-3033" data-mp="16h2yezkf8t9o">
          <Panel title="Kleur" data-id="element-3034" data-mp="16iw9cr22hznh">
            <PaletteSelect value={design.palette} onChange={palette => set({
            palette
          })} data-id="element-3035" data-mp="rsgj2xuso1ig" />
          </Panel>

          <Panel title="Tekst" description="Tip: {firstName}, {years}, {city} en {phone} volgen je instellingen." data-id="element-3036" data-mp="wq891mbvpdk0">
            <div className="space-y-5" data-id="element-3037" data-mp="iqfne5f7lniw">
              {textInput('eyebrow')}
              {textInput('headline')}
              {textInput('accent')}
              {textInput('body')}
              {has('points') && <Field label={design.template === 'giftcard' ? 'Invulregels' : 'Punten'} hint="Eén per regel." data-id="element-3038" data-mp="8uvs9l8gysv7">
                  <textarea value={design.points.join('\n')} onChange={e => set({
                points: e.target.value.split('\n')
              })} rows={Math.max(3, design.points.length + 1)} className={`${inputCls} resize-y leading-relaxed`} data-id="element-3039" data-mp="du8imfyjui2j" />
                </Field>}
              {textInput('cta')}
            </div>
          </Panel>

          {has('productId') && <Panel title="Product" description="Foto en prijs komen uit de webshop." data-id="element-3040" data-mp="69obb3ja45ua">
              <select value={design.productId ?? ''} onChange={e => set({
            productId: e.target.value || undefined
          })} className={inputCls} aria-label="Product" data-id="element-3041" data-mp="1hmrmwjs7cu8">
                <option value="" data-id="element-3042" data-mp="b9sks9lrs0b0">Kies een product</option>
                {catalog.products.map(p => <option key={p.id} value={p.id} data-id="element-3043" data-mp="p5ef8warvcat">
                    {p.brand} — {p.name}
                  </option>)}
              </select>
            </Panel>}

          {has('routineId') && <Panel title="Protocol" description="Producten en totaalprijs komen uit het protocol." data-id="element-3044" data-mp="2m00vwaqs7iad">
              <select value={design.routineId ?? ''} onChange={e => set({
            routineId: e.target.value || undefined
          })} className={inputCls} aria-label="Protocol" data-id="element-3045" data-mp="36l901m665j62">
                {catalog.routines.map(r => <option key={r.id} value={r.id} data-id="element-3046" data-mp="2slr7cvpr5lrf">
                    {r.name}
                  </option>)}
              </select>
            </Panel>}

          {has('image') && <Panel title="Foto" data-id="element-3047" data-mp="3kd6woxip1mlc">
              <ul className="mb-4 grid grid-cols-4 gap-2 sm:grid-cols-5" data-id="element-3048" data-mp="3ke8ah9r3gn6r">
                {photos.map(ph => <li key={ph.src} data-id="element-3049" data-mp="3fkdvgjmfowe0">
                    <button type="button" onClick={() => set({
                image: ph.src
              })} aria-label={`Foto kiezen: ${ph.label}`} aria-pressed={design.image === ph.src} title={ph.label} className={`block aspect-square w-full overflow-hidden rounded-md transition-shadow duration-150 ease-soft ${design.image === ph.src ? 'ring-2 ring-ink ring-offset-2 ring-offset-white' : 'ring-1 ring-line hover:ring-ink/40'}`} data-id="element-3050" data-mp="3bklmi5t4oo84">
                      <ProductImage src={ph.src} fit="cover" className="h-full w-full" data-id="element-3051" data-mp="23sodq52ankyi" />
                    </button>
                  </li>)}
              </ul>
              <ImageField value={design.image} onChange={image => set({
            image
          })} fit="cover" aspect="aspect-[4/3]" data-id="element-3052" data-mp="28q6m9ucefy5z" />
            </Panel>}

          {has('qr') && <Panel title="QR-code" description="Scant iemand de code, dan komt ze op deze pagina. Leeg = geen QR-code." data-id="element-3053" data-mp="2iim48rnp4mqv">
              <input value={design.qrUrl} onChange={e => set({
            qrUrl: e.target.value
          })} placeholder="https://beautybewust.nl/behandelingen" className={inputCls} aria-label="Link achter de QR-code" data-id="element-3054" data-mp="2ml8nw5a7r1cz" />
            </Panel>}

          {t.channel !== 'Print' && <Panel title={t.channel === 'E-mail' ? 'Onderwerpregel en tekst' : 'Tekst bij het bericht'} actions={fullCaption ? <CopyButton text={fullCaption} label="Kopieer tekst" data-id="element-3056" data-mp="11efdt3ajhoyc" /> : undefined} data-id="element-3055" data-mp="rhu6uceym3rv">
              <div className="space-y-5" data-id="element-3057" data-mp="1f5twta4g5q5e">
                <Field label="Tekst" count={{
              value: fullCaption.length,
              max: CAPTION_MAX
            }} data-id="element-3058" data-mp="1f4smj7v997oq">
                  <textarea value={design.caption} onChange={e => set({
                caption: e.target.value
              })} rows={7} placeholder="Wat wil je erbij vertellen?" className={`${inputCls} resize-y leading-relaxed`} data-id="element-3059" data-mp="1b3712y4nnl2x" />
                </Field>
                <Field label="Hashtags" as="div" data-id="element-3060" data-mp="3qx7ndrrraq8t">
                  <TagInput label="Hashtags" value={design.hashtags} onChange={hashtags => set({
                hashtags: hashtags.map(h => h.startsWith('#') ? h : `#${h}`)
              })} suggestions={hashtagPool.slice(0, HASHTAG_SUGGESTIONS)} data-id="element-3061" data-mp="3lxbjdsj26yt9" />
                </Field>
              </div>
            </Panel>}

          <Panel title="Ordenen" data-id="element-3062" data-mp="1wmh9s2ds2j5c">
            <div className="grid gap-5 sm:grid-cols-2" data-id="element-3063" data-mp="1wlpslwkxgnyl">
              <Field label="Naam in het overzicht" className="sm:col-span-2" data-id="element-3064" data-mp="1inhw1prszony">
                <input value={design.name} onChange={e => set({
                name: e.target.value
              })} className={inputCls} data-id="element-3065" data-mp="1nmku2q9d8umf" />
              </Field>
              <Field label="Campagne" data-id="element-3066" data-mp="18qvdx8qv3bpm">
                <input value={design.campaign} onChange={e => set({
                campaign: e.target.value
              })} list="campagnes" className={inputCls} data-id="element-3067" data-mp="18ry39c8jd63f" />
                <datalist id="campagnes" data-id="element-3068" data-mp="333cu5wda9bkr">
                  {campaigns.map(c => <option key={c} value={c} data-id="element-3069" data-mp="3837lu24vu56v" />)}
                </datalist>
              </Field>
              <Field label="Sjabloon" hint="Je tekst gaat mee naar het nieuwe sjabloon." data-id="element-3070" data-mp="180py6ke02tbo">
                <select value={design.template} onChange={e => isTemplateKey(e.target.value) && set({
                template: e.target.value
              })} className={inputCls} data-id="element-3071" data-mp="13ybbfp77n9f4">
                  {templates.map(x => <option key={x.key} value={x.key} data-id="element-3072" data-mp="p1kcfh1g7zq1">
                      {x.name} ({x.channel})
                    </option>)}
                </select>
              </Field>
            </div>
            <div className="mt-6 border-t border-line pt-4" data-id="element-3073" data-mp="g90l71nhkxf8">
              <DeleteButton undoable label="Ontwerp verwijderen"
            // Posts en nieuwsbrieven met dit ontwerp zouden stil hun beeld verliezen: dat eerst zeggen.
            warning={usage.length ? `Zit in ${usage.join(' en ')}; die verliezen hun beeld.` : undefined} onConfirm={() => {
              const at = marketing.designs.findIndex(d => d.id === id);
              const removed = marketing.designs[at];
              setMarketing(m => ({
                ...m,
                designs: m.designs.filter(d => d.id !== id)
              }));
              navigate('/be/ontwerpen');
              if (!removed) return;
              undoToast(`“${removed.name || 'Naamloos ontwerp'}” verwijderd`, () => setMarketing(m => {
                if (m.designs.some(d => d.id === id)) return m;
                const designs = [...m.designs];
                designs.splice(Math.min(at, designs.length), 0, removed);
                return {
                  ...m,
                  designs
                };
              }), {
                description: 'Posts en nieuwsbrieven met dit ontwerp krijgen hun beeld terug.',
                restored: 'Ontwerp teruggezet'
              });
            }} data-id="element-3074" data-mp="g9idjn712rhv" />
            </div>
          </Panel>
        </div>
      </div>
    </div>;
}